import Phaser from 'phaser';
import type { SaveManager } from '@/game/systems/SaveManager';
import type { AudioManager } from '@/game/systems/AudioManager';
import { CATS } from '@/data/cats';
import { createCatIcon } from '@/game/ui/CatIcon';
import { INTERNAL_HEIGHT, INTERNAL_WIDTH } from '@/game/config/GameConfig';
import { PALETTE, animateCoinSpend, bodyTextStyle, createButton, createCoinPanel, titleTextStyle } from '@/game/ui/theme';

type Lane = 'left' | 'right';

export class CatSelectScene extends Phaser.Scene {
  private saveManager!: SaveManager;
  private audioManager!: AudioManager;
  private coinText!: Phaser.GameObjects.Text;

  constructor() {
    super('CatSelectScene');
  }

  create(): void {
    this.saveManager = this.game.registry.get('saveManager');
    this.audioManager = this.game.registry.get('audioManager');

    this.add.rectangle(0, 0, INTERNAL_WIDTH, INTERNAL_HEIGHT, PALETTE.bgBottom).setOrigin(0);
    this.add.text(INTERNAL_WIDTH / 2, 66, 'Choose Your Cats', titleTextStyle(38)).setOrigin(0.5);

    const rowGap = 77;
    const leftBottom = this.buildLaneRow('left', 'Left Cat (A / D)', 210);
    this.buildLaneRow('right', 'Right Cat (← / →)', leftBottom + rowGap + 90);

    const backBtn = createButton(this, 90, 66, 120, 60, 'Back', PALETTE.panelLight, 22);
    backBtn.on('pointerup', () => this.scene.start('HomeScene'));

    this.coinText = createCoinPanel(this, INTERNAL_WIDTH - 110, 44, this.saveManager.getData().coins);
  }

  /** Lays out one lane's cat grid starting at rowY and returns the Y coordinate of
   * its lowest content (last row's price/name text), so the next lane's row can be
   * positioned below it regardless of how many rows the roster now needs. */
  private buildLaneRow(lane: Lane, label: string, rowY: number): number {
    this.add.text(60, rowY - 90, label, bodyTextStyle(24, '#e9d9ff')).setOrigin(0, 0.5);

    const cols = 4;
    const spacingX = 170;
    const startX = INTERNAL_WIDTH / 2 - ((cols - 1) * spacingX) / 2;
    const spacingY = 185;
    const rows = Math.ceil(CATS.length / cols);

    CATS.forEach((cat, i) => {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const x = startX + col * spacingX;
      const y = rowY + row * spacingY;

      const unlocked = this.saveManager.isCatUnlocked(cat.id);
      const selectedId = lane === 'left' ? this.saveManager.getData().selectedLeftCatId : this.saveManager.getData().selectedRightCatId;

      if (cat.id === selectedId) {
        this.add.circle(x, y, 66, 0x000000, 0).setStrokeStyle(4, PALETTE.accent, 1);
      }

      const icon = createCatIcon(this, x, y, cat, unlocked ? 0.28 : 0.24);
      if (!unlocked) icon.setAlpha(0.45);

      this.add.text(x, y + 76, cat.name, bodyTextStyle(18)).setOrigin(0.5);
      if (!unlocked) {
        this.add.text(x, y + 98, `${cat.price} coins`, bodyTextStyle(15, '#ffe066')).setOrigin(0.5);
      }

      const hitZone = this.add.zone(x, y, 132, 150).setInteractive({ useHandCursor: true });
      hitZone.on('pointerup', () => this.onCatTapped(lane, cat.id, cat.price));
    });

    return rowY + (rows - 1) * spacingY + 98;
  }

  private onCatTapped(lane: Lane, catId: string, price: number): void {
    const unlocked = this.saveManager.isCatUnlocked(catId);
    if (!unlocked) {
      const before = this.saveManager.getData().coins;
      if (this.saveManager.spendCoins(price)) {
        this.saveManager.unlockCat(catId);
        this.audioManager.playUnlock();
        animateCoinSpend(this, this.coinText, before, this.saveManager.getData().coins, () => this.scene.restart());
      }
      return;
    }
    this.saveManager.setSelectedCat(lane, catId);
    this.scene.restart();
  }
}
